import { Loader2 } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { useDataSource } from '@/hooks/use-data-sources';

interface SchemaField {
  name: string;
  type?: string;
}

interface SchemaPreviewProps {
  dataSourceId: string;
}

export function SchemaPreview({ dataSourceId }: SchemaPreviewProps) {
  const { data: dataSource, isLoading } = useDataSource(dataSourceId);

  if (isLoading) {
    return (
      <div className="p-3 flex items-center justify-center">
        <Loader2 className="h-4 w-4 animate-spin" />
      </div>
    );
  }

  if (!dataSource) return null;

  const metadata = dataSource.metadata as Record<string, unknown> | undefined;
  const schema = (metadata?.schema ?? metadata?.columns ?? []) as (string | SchemaField)[];
  const sampleData = (metadata?.sampleData ?? metadata?.preview ?? []) as Record<string, unknown>[];

  function formatField(field: string | SchemaField): string {
    if (typeof field === 'string') return field;
    return `${field.name}:${field.type ?? '?'}`;
  }

  return (
    <div className="max-h-[200px] overflow-y-auto">
      <div className="p-3 space-y-2">
        <h4 className="text-xs font-semibold text-muted-foreground">Schema</h4>

        {schema.length > 0 ? (
          <div className="flex flex-wrap gap-1">
            {schema.slice(0, 10).map((field, i) => (
              <Badge key={i} variant="outline" className="text-xs font-mono">
                {formatField(field)}
              </Badge>
            ))}
            {schema.length > 10 && (
              <Badge variant="secondary" className="text-xs">
                +{schema.length - 10} more
              </Badge>
            )}
          </div>
        ) : (
          <p className="text-xs text-muted-foreground">No schema available</p>
        )}

        {sampleData.length > 0 && (
          <SampleDataTable rows={sampleData} />
        )}
      </div>
    </div>
  );
}

interface SampleDataTableProps {
  rows: Record<string, unknown>[];
}

function SampleDataTable({ rows }: SampleDataTableProps) {
  const columns = Object.keys(rows[0]).slice(0, 5);

  return (
    <>
      <h4 className="text-xs font-semibold text-muted-foreground mt-2">Sample Data</h4>
      <div className="text-xs overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow>
              {columns.map((key) => (
                <TableHead key={key} className="text-xs h-7 px-2">
                  {key}
                </TableHead>
              ))}
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.slice(0, 3).map((row, i) => (
              <TableRow key={i}>
                {columns.map((key) => (
                  <TableCell key={key} className="text-xs py-1 px-2">
                    {String(row[key] ?? '')}
                  </TableCell>
                ))}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </>
  );
}
